import Phaser from 'phaser';
import Piano from '../GameObjects/Piano';
import { ReactToPhaserListeners, NotifyReactEvents, RemoveListener } from 'Phaser/EventBus2';

/**
 * PianoDebugScene — a stripped-down piano sandbox for the Debug Dummy MIDI
 * feature (FeatureFlag.DEBUG_DUMMY_MIDI). Derived from PianoScene but with all
 * the "Tutorial Exercise" machinery (ExerciseBase, objectives, validators,
 * instructions, transitions) removed — only piano rendering and keypress
 * highlighting remain, so a developer can visually confirm the
 * computer-keyboard → MIDI mapping without a physical device.
 *
 * Note input arrives through the normal MidiContext pipeline (including the
 * DEBUG_DUMMY_MIDI keyboard injection) which the React container forwards to
 * Phaser as MidiOn/MidiOff events — the same path real exercises use.
 */
export class PianoDebugScene extends Phaser.Scene {
  private piano?: Piano;

  constructor() {
    super({ key: 'PianoDebugScene' });
  }

  create() {
    const { width, height } = this.cameras.main;

    this.add
      .text(width / 2, height * 0.2, 'MIDI Keyboard Debug', {
        fontFamily: 'Lato, sans-serif',
        fontSize: '64px',
        color: '#ECECEC',
      })
      .setOrigin(0.5);

    this.add
      .text(
        width / 2,
        height * 0.29,
        'Home row = white keys (A → E3)     •     top row = black keys (E → F♯3)',
        { fontFamily: 'Lato, sans-serif', fontSize: '34px', color: '#8AA0B6' },
      )
      .setOrigin(0.5);

    this.add
      .text(
        width / 2,
        height * 0.35,
        'Z / X = transpose down / up (keeps home row on white keys)',
        {
          fontFamily: 'Lato, sans-serif',
          fontSize: '28px',
          color: '#5E7A88',
        },
      )
      .setOrigin(0.5);

    // Piano adds itself to the scene inside its constructor (scene.add.existing).
    this.piano = new Piano(this, width / 2, height / 2, 'C2', 'C6');
    this.piano.setScale(0.18);

    // The default validator returns true for every key, so onPressKey highlights
    // each pressed key green — exactly the visual feedback we want here.
    ReactToPhaserListeners.MidiOn((data) => this.piano?.onPressKey(data.note), this);
    ReactToPhaserListeners.MidiOff((data) => this.piano?.onReleaseKey(data.note), this);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      RemoveListener('MidiOn');
      RemoveListener('MidiOff');
      this.piano?.destroy();
      this.piano = undefined;
    });

    NotifyReactEvents.CurrentSceneReady(this);
  }
}

export default PianoDebugScene;
